/**
 * 特集ページで使う共通コンポーネント群
 */
import { Box, useToast } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import Breadcrumbs from '../components/Breadcrumbs';
import PageHead from '../components/PageHead';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import NeedLogin from '../components/NeedLogin';
import PageUnavailableMsg from '../components/PageUnavailableMsg';
import NeedLicenseVerify from '../components/NeedLicenseVerify';
import { shouldShowSpecialPage } from '../common/libs/specials';

function useSpecialPage({
  specialPageTitle,
  gaLabelKey,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isAllowAccount = false, // ホワイトリストに含まれるアカウントかどうか
  mainImagePath,
  specialPagePath,
  redirectPath,
  isPrivate,
  needLogin = true,
  needLicenseVerified = true,
  specialPageSlug,
}: {
  specialPageTitle: string;
  gaLabelKey: string;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isAllowAccount?: boolean;
  mainImagePath: string;
  specialPagePath: string;
  redirectPath?: string; // NeedLoginに渡すredirectUrlがspecialPagePathと異なるときに使用
  isPrivate: boolean;
  needLogin?: boolean;
  needLicenseVerified?: boolean;
  specialPageSlug?: string;
}): {
  isBlocked: boolean; // ログインユーザーの状態、種類により表示されるコンテンツがブロックされているか
  pageHead: JSX.Element; // 特集ページの<head/>タグ
  breadcrumbs: JSX.Element; // パンくずリストのコンポーネント
  blockedContent: JSX.Element | null; // ブロックされた場合のコンテンツ
} {
  const toast = useToast();

  useEffect(() => {
    if (isPrivate) {
      toast({
        title: 'プレビュー',
        description:
          'このページはプレビューです。ユーザーには公開されていません',
        status: 'success',
        isClosable: true,
        variant: 'subtle',
        position: 'top',
        duration: 100000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate]);

  // 特集ページマウント時にログイン状態によりイベント送信
  useEffect(() => {
    if (
      isAuthenticated === undefined ||
      isLicenseVerified === undefined ||
      !gaLabelKey
    ) {
      return;
    }
    const gaLabel =
      isAuthenticated && isLicenseVerified
        ? gaLabelKey
        : `${gaLabelKey}_no_login`;
    sendAnalyticsEvent('special_contents', 'show', gaLabel);
  }, [isAuthenticated, isLicenseVerified, gaLabelKey]);

  // 特集ページのheadタグ
  const pageHead = (
    <PageHead
      description={specialPageTitle}
      imgUrlSuffix={mainImagePath}
      pageUrlSuffix={specialPagePath}
    />
  );

  // ログイン状態等により表示されるコンテンツ
  const blockedContent = isAuthenticated ? ( // ログインしている時
    // ホワイトリストに含まれるアカウントの場合は特例で表示
    isAllowAccount ? null : !shouldShowSpecialPage(
        // アカウントタイプによる認可チェック
        specialPageSlug,
        isCorporationAccount,
        isPublisherAccount,
        isDoctorRecruiterAccount,
        isDemoAccount,
      ) ? (
      <PageUnavailableMsg isDemoAccount={isDemoAccount} />
    ) : needLicenseVerified && !isLicenseVerified ? (
      <>
        {pageHead}
        <Box pt={[20]} bg="white">
          <NeedLicenseVerify gaLabelKey={`sp_${gaLabelKey}`} />
        </Box>
      </>
    ) : null
  ) : needLogin ? ( // ログインしていない時
    <>
      {pageHead}
      <Box pt={[20]} bg="white">
        <NeedLogin
          gaLabelKey={`sp_${gaLabelKey}`}
          redirectUrl={redirectPath ?? specialPagePath}
        />
      </Box>
    </>
  ) : null;

  const breadcrumbs = (
    <Box py={4} pl={[4, 8, 4]} bg="white" w={{ base: '1024px', sm: '1024px' }}>
      <Breadcrumbs
        parentPages={[
          {
            title: '特集',
            href: '/special',
            gaLabel: 'special',
          },
        ]}
        currentPageTitle={specialPageTitle}
      />
    </Box>
  );

  return {
    isBlocked: !!blockedContent,
    blockedContent,
    pageHead,
    breadcrumbs,
  };
}

export default useSpecialPage;
